import { computed } from 'vue';
import { defineStore } from 'pinia';
import { useAuth0 } from '@auth0/auth0-vue';
import { useStorage } from '@peanuds/composables';
import { publicPeanudsApi } from '@peanuds/shared';

import { authEventBus } from '../auth-event-bus';
import {
  getAuth0LoginRedirectOptions,
  getRedirectTargetQueryParams,
  type OrganizationInvitationParams,
} from '../auth0-login-options';
import {
  getStoredPostLoginRedirectTarget,
  resolvePostLoginRedirectTarget,
  storePostLoginRedirectTarget,
} from '../post-login-redirect-target';
import { publishAuthLogoutToTabs } from '../composables/useAuthIdentityTabSync';
import { useAuthSessionStore } from './auth-session.store';

export type AuthRedirectMode = 'fresh' | 'resume';

const SESSION_REVOCATION_TIMEOUT_MS = 5_000;

interface AuthLoginRedirectOptions {
  redirectTarget?: unknown;
  mode?: AuthRedirectMode;
  invitation?: OrganizationInvitationParams;
}

interface AuthSignupRedirectOptions {
  redirectTarget?: unknown;
}

export const useAuthStore = defineStore('auth', () => {
  const auth0 = useAuth0();
  const auth0Logout = auth0?.logout;
  const auth0LoginWithRedirect = auth0?.loginWithRedirect;

  const session = useAuthSessionStore();
  const { removeAll } = useStorage<string>('auth', { storage: 'local' });

  const jwtToken = computed(() => session.jwtToken);
  const isLoggedIn = computed(() => session.isLoggedIn);
  const isAuthenticatedInIf = computed(() => session.isAuthenticatedInIf);
  const lastActiveTime = computed(() => session.lastActiveTime);
  const sessionActiveUntil = computed(() => session.sessionActiveUntil);

  const clearBrowserAuthState = () => {
    session.clearAuth();
    sessionStorage.clear();
    removeAll();
  };

  const getCurrentRedirectTarget = () => {
    if (typeof window === 'undefined') {
      return '/';
    }

    const redirectTarget = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    return resolvePostLoginRedirectTarget(redirectTarget);
  };

  const getMfaRedirectTarget = () => {
    if (window.location.pathname === '/login') {
      return resolvePostLoginRedirectTarget(
        new URLSearchParams(window.location.search).get('redirect'),
      );
    }

    if (window.location.pathname.startsWith('/auth/')) {
      return getStoredPostLoginRedirectTarget();
    }

    return getCurrentRedirectTarget();
  };

  /**
   * Clears all local auth state without triggering the Auth0 logout redirect.
   * Use this before starting a fresh login redirect when we only want to reset stale client state.
   */
  async function clearLocalSession() {
    await authEventBus.emit('logout');
    clearBrowserAuthState();
  }

  function clearLocalSessionSync() {
    clearBrowserAuthState();
  }

  /**
   * Logout user from both local state and Auth0.
   * Emits logout event, clears localStorage, and redirects to Auth0 logout.
   */
  async function logout() {
    if (session.jwtToken && session.idToken && !session.isTokenExpired) {
      const abortController = new AbortController();
      const abortTimeout = setTimeout(() => abortController.abort(), SESSION_REVOCATION_TIMEOUT_MS);

      try {
        await publicPeanudsApi.deleteCurrentSession({
          headers: {
            Authorization: `Bearer ${session.jwtToken}`,
            'X-Id-Token': `Bearer ${session.idToken}`,
          },
          signal: abortController.signal,
        } satisfies Parameters<typeof publicPeanudsApi.deleteCurrentSession>[0]);
      } catch {
        // Backend revocation must not prevent local and Auth0 logout.
      } finally {
        clearTimeout(abortTimeout);
      }
    }

    await clearLocalSession();
    publishAuthLogoutToTabs();

    auth0Logout?.({
      logoutParams: {
        returnTo: `${window.location.origin}/logout`,
      },
    });
  }

  const redirectToAuth0 = async (
    redirectTarget: unknown,
    mode: AuthRedirectMode,
    options?: { screenHint?: 'signup'; invitation?: OrganizationInvitationParams },
  ) => {
    if (!auth0LoginWithRedirect) {
      throw new Error('Missing Auth0 login redirect handler');
    }

    const organizationId = mode === 'resume' ? (session.organizationId ?? undefined) : undefined;

    await clearLocalSession();
    const storedRedirectTarget = storePostLoginRedirectTarget(redirectTarget);
    const customParams = getRedirectTargetQueryParams(storedRedirectTarget);

    await auth0LoginWithRedirect(
      mode === 'resume'
        ? getAuth0LoginRedirectOptions(organizationId, {
            customParams,
            invitation: options?.invitation,
          })
        : getAuth0LoginRedirectOptions(undefined, {
            forceFreshLogin: true,
            customParams,
            invitation: options?.invitation,
            ...(options?.screenHint ? { screenHint: options.screenHint } : {}),
          }),
    );
  };

  const redirectToLogin = async (options: AuthLoginRedirectOptions = {}) => {
    const redirectTarget =
      options.redirectTarget === undefined ? getCurrentRedirectTarget() : options.redirectTarget;

    await redirectToAuth0(redirectTarget, options.mode ?? 'fresh', {
      invitation: options.invitation,
    });
  };

  const redirectToSignup = async (options: AuthSignupRedirectOptions = {}) => {
    const redirectTarget =
      options.redirectTarget === undefined ? getCurrentRedirectTarget() : options.redirectTarget;

    await redirectToAuth0(redirectTarget, 'fresh', { screenHint: 'signup' });
  };

  const redirectToMfaLogin = async () => {
    if (!auth0LoginWithRedirect) {
      throw new Error('Missing Auth0 login redirect handler');
    }

    const redirectTarget = getMfaRedirectTarget();
    const organizationId = session.requiredOrganizationId ?? session.organizationId ?? undefined;

    await clearLocalSession();
    storePostLoginRedirectTarget(redirectTarget);

    await auth0LoginWithRedirect(
      getAuth0LoginRedirectOptions(organizationId, { forceFreshLogin: true }),
    );
  };

  return {
    jwtToken,
    isLoggedIn,
    isAuthenticatedInIf,
    lastActiveTime,
    sessionActiveUntil,
    setLastActiveTime: session.setLastActiveTime,
    clearLocalSession,
    clearLocalSessionSync,
    logout,
    redirectToLogin,
    redirectToSignup,
    redirectToMfaLogin,
  };
});
